import React from "react";

import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import { Box, Paper, Stack, Tooltip, Typography } from "@mui/material";
import { alpha, useTheme } from "@mui/material/styles";

import { FEATURE_CARD_ACCENT_WIDTH } from "@shell/styles/designTokens";

import { CloudProvider } from "../../types";

interface Props {
  cloudProvider: CloudProvider | null;
  onCloudProviderChange: (provider: CloudProvider) => void;
}

interface ProviderOption {
  provider: CloudProvider;
  label: string;
  description: string;
  storageLabel: string;
  freeTierNote?: string;
}

const PROVIDER_OPTIONS: ProviderOption[] = [
  {
    provider: CloudProvider.AWS,
    label: "Amazon Web Services",
    description: "Deploy on AWS infrastructure",
    storageLabel: "S3 · ECS · RDS · Glue · Kinesis · Lambda",
  },
  {
    provider: CloudProvider.GCP,
    label: "Google Cloud Platform",
    description: "Deploy on GCP infrastructure",
    storageLabel: "GCS · Cloud Run · BigQuery · Dataproc · Pub/Sub",
  },
  {
    provider: CloudProvider.AZURE,
    label: "Microsoft Azure",
    description: "Deploy on Azure infrastructure",
    storageLabel: "ADLS · App Service · Synapse · Cosmos DB · Event Hubs",
  },
  {
    provider: CloudProvider.SELF_HOSTED,
    label: "Self-hosted",
    description: "Deploy on your own servers or bare metal",
    storageLabel: "MinIO · k3s / RKE2 · PostgreSQL · Redis",
  },
  {
    provider: CloudProvider.RUSHA_HOSTED,
    label: "Rusha Hosted",
    description: "Fully managed by Rusha — zero infra to configure",
    storageLabel: "Managed K8s · Auto-scaling · Included storage",
    freeTierNote:
      "Counts toward free tier stack limit. Paid billing is metered per CPU/RAM hour.",
  },
];

export function StepChooseCloud({
  cloudProvider,
  onCloudProviderChange,
}: Props) {
  const theme = useTheme();

  function handleProviderClick(provider: CloudProvider) {
    onCloudProviderChange(provider);
  }

  const selectionCardSx = (selected: boolean) => ({
    cursor: "pointer",
    borderRadius: 1,
    border: `1px solid ${selected ? theme.palette.primary.main : theme.palette.divider}`,
    borderTopWidth: selected ? FEATURE_CARD_ACCENT_WIDTH : 1,
    borderTopColor: selected
      ? theme.palette.primary.main
      : theme.palette.divider,
    bgcolor: selected
      ? alpha(theme.palette.primary.main, 0.03)
      : "background.paper",
    transition: "border-color 0.15s, background-color 0.15s, transform 0.15s",
    "&:hover": {
      borderColor: theme.palette.primary.main,
      transform: "translateY(-2px)",
    },
    "&:focus-visible": {
      outline: "2px solid",
      outlineColor: theme.palette.primary.main,
      outlineOffset: 2,
    },
  });

  return (
    <Box>
      <Typography variant="subtitle1" fontWeight={600} gutterBottom>
        Choose cloud provider
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
        Select where your stack will run. Infrastructure is configured at the
        project level.
      </Typography>

      {/* Provider cards */}
      <Stack direction="row" flexWrap="wrap" gap={2}>
        {PROVIDER_OPTIONS.map((opt) => {
          const selected = cloudProvider === opt.provider;
          return (
            <Paper
              key={opt.provider}
              variant="outlined"
              role="button"
              tabIndex={0}
              aria-label={`Select ${opt.label}`}
              onClick={() => handleProviderClick(opt.provider)}
              onKeyDown={(e) => {
                if (e.key === "Enter" || e.key === " ") {
                  e.preventDefault();
                  handleProviderClick(opt.provider);
                }
              }}
              sx={{
                width: { xs: "100%", sm: "calc(50% - 8px)" },
                p: 2,
                ...selectionCardSx(selected),
              }}
            >
              <Stack
                direction="row"
                spacing={0.5}
                alignItems="center"
                sx={{ mb: 0.5 }}
              >
                <Typography variant="subtitle1" fontWeight={600}>
                  {opt.label}
                </Typography>
                {opt.freeTierNote && (
                  <Tooltip title={opt.freeTierNote} placement="top" arrow>
                    <InfoOutlinedIcon
                      sx={{ fontSize: 16, color: "text.disabled" }}
                    />
                  </Tooltip>
                )}
              </Stack>
              <Typography variant="body2" color="text.secondary">
                {opt.description}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                {opt.storageLabel}
              </Typography>
            </Paper>
          );
        })}
      </Stack>
    </Box>
  );
}
